import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { env } from "@/lib/env";

export interface AgentQuestionOption {
	label: string;
	description?: string;
}

export interface AgentQuestion {
	question: string;
	options?: AgentQuestionOption[];
	/**
	 * "notice" is a progress report — the agent is telling you what it just did
	 * and is not waiting for a reply. Absent means it is a real question.
	 */
	kind?: "question" | "notice" | "delete-confirmation";
	/** Sent back with the answer so it counts only for this question. */
	questionId?: string;
	/** kind "delete-confirmation": what will be deleted. Only its Delete/Cancel buttons answer it, and the chat is blocked meanwhile. */
	deletion?: { level: "campaign" | "adset" | "ad" | "creative"; id: string; name: string | null };
}

/** The agent waits for a Delete or Cancel press: no other message can be sent. */
export const isDeletionQuestion = (q: AgentQuestion | null | undefined) => q?.kind === "delete-confirmation";

const SILENT_CHANNEL_MS = 20_000;
const WATCHDOG_INTERVAL_MS = 5_000;

/**
 * Live channel for what the Meta Ads agent says mid-run.
 *
 * Subscribes to `GET /api/sse-chat/agent-question/:conversationId`, which
 * carries two things: questions that block the run until answered, and progress
 * reports after each object is created. Only the questions surface as
 * `pendingQuestion`; both land in conversation history.
 *
 * The backend persists every payload, so the text arrives through the normal
 * chatHistory refetch too — what this hook adds is the live push and the
 * `options`, which are never stored in history.
 *
 * Answering is a plain chat message carrying the question's id: call
 * `clearQuestion()` just before sending and pass the id it returns. The
 * backend accepts it only for that question; a stale one comes back as 409
 * `question_not_pending` (or `reload_required` without an id). `resync()`
 * reconnects, and the server sends the question still waiting, with its id.
 * Any other message sent while a run is active comes back as 409 `agent_busy`.
 */
export function useAgentQuestions(projectId: string | undefined) {
	const [pendingQuestion, setPendingQuestion] = useState<AgentQuestion | null>(
		null,
	);
	const queryClient = useQueryClient();
	const sourceRef = useRef<EventSource | null>(null);
	const questionIdRef = useRef<string | undefined>(undefined);
	const reopenRef = useRef<() => void>(() => {});

	useEffect(() => {
		if (!projectId) return;

		const refetchHistory = () =>
			queryClient.invalidateQueries({ queryKey: ["chatHistory", projectId] });
		let source: EventSource | null = null;
		let lastEventAt = Date.now();

		const onQuestion = (e: Event) => {
			lastEventAt = Date.now();
			try {
				const payload = JSON.parse((e as MessageEvent).data) as AgentQuestion;

				// Both questions and progress reports are persisted server-side, so
				// refetch first — this has to happen even for payloads that never
				// become a prompt, or the text would only appear after a reload.
				refetchHistory();

				// A notice is the agent reporting what it created. Nothing is waiting
				// on the user, so it stays an ordinary message in the thread. A run
				// only ever notifies between questions, so an open prompt is stale by
				// then — a stopped run reports through this path and its question
				// would otherwise stay on screen with nobody listening.
				if (!payload.question || payload.kind === "notice") {
					questionIdRef.current = undefined;
					setPendingQuestion(null);
					return;
				}

				questionIdRef.current = payload.questionId;
				setPendingQuestion(payload);
			} catch {
				// malformed payload — ignore, history refetch will still surface it
			}
		};

		const open = () => {
			source?.close();
			lastEventAt = Date.now();
			source = new EventSource(
				`${env.API_URL}/api/sse-chat/agent-question/${projectId}`,
				{ withCredentials: true },
			);
			sourceRef.current = source;
			source.addEventListener("agent-question", onQuestion);
			// Pushes sent while the channel was down (server restart, dropped
			// connection) are gone: catch up from history on every (re)connect.
			source.addEventListener("connected", (e: Event) => {
				lastEventAt = Date.now();
				refetchHistory();
				// The ads agent has nothing waiting (a restart ended the run): drop its stale prompt.
				// Only prompts with an id are the ads agent's; campaign questions have none.
				try {
					const data = JSON.parse((e as MessageEvent).data) as { questionWaiting?: boolean };
					if (data.questionWaiting === false && questionIdRef.current) {
						questionIdRef.current = undefined;
						setPendingQuestion(null);
					}
				} catch {
					// older server without the flag: keep the prompt
				}
			});
			source.addEventListener("heartbeat", () => {
				lastEventAt = Date.now();
			});
		};
		open();
		reopenRef.current = open;

		// The server beats every 5 s. This much silence means the channel died
		// without an error event, so EventSource will not reconnect by itself.
		const watchdog = setInterval(() => {
			if (Date.now() - lastEventAt > SILENT_CHANNEL_MS) open();
		}, WATCHDOG_INTERVAL_MS);

		const onVisible = () => {
			if (document.visibilityState === "visible") refetchHistory();
		};
		document.addEventListener("visibilitychange", onVisible);

		return () => {
			clearInterval(watchdog);
			document.removeEventListener("visibilitychange", onVisible);
			source?.close();
			sourceRef.current = null;
			reopenRef.current = () => {};
			questionIdRef.current = undefined;
			setPendingQuestion(null);
		};
	}, [projectId, queryClient]);

	/** Clears the prompt and returns the id of the question it showed, for the answer. */
	const clearQuestion = useCallback(() => {
		const id = questionIdRef.current;
		questionIdRef.current = undefined;
		setPendingQuestion(null);
		return id;
	}, []);
	const resync = useCallback(() => reopenRef.current(), []);

	return { pendingQuestion, clearQuestion, resync };
}
